/**
 * Pilot Rollout Guidance
 * ======================
 * Controlled commercial pilot deployment procedures.
 * 
 * Route: /wyldecrest/helpdesk/pilot-rollout
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  Rocket, 
  Target, 
  Shield, 
  CheckCircle,
  Users,
  BarChart
} from 'lucide-react';

const sections = [
  { id: 'pilot-overview', title: 'Pilot Overview' },
  { id: 'readiness-criteria', title: 'Readiness Criteria' },
  { id: 'rollout-phases', title: 'Rollout Phases' },
  { id: 'monitoring-kpis', title: 'Monitoring & KPIs' },
  { id: 'escalation-support', title: 'Escalation & Support' },
];

export default function PilotRolloutPage() {
  return (
    <GovernancePageLayout
      title="Pilot Rollout Guidance"
      subtitle="Controlled commercial pilot deployment procedures"
      icon={Rocket}
      version="1.0"
      lastReviewed="2026-05-23"
      reviewedBy="Operations Director"
      sections={sections}
    >
      {/* Pilot Overview */}
      <DocumentSection title="Pilot Overview" icon={Target} id="pilot-overview">
        <p className="text-slate-300 mb-6">
          The controlled commercial pilot is a phased rollout to validate the platform
          with real clients before full commercial launch.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Pilot Objectives</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Validate</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Platform stability under real load</li>
              <li>• User experience meets expectations</li>
              <li>• SLA compliance achievable</li>
              <li>• Integration reliability</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Refine</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Operational processes</li>
              <li>• Support procedures</li>
              <li>• Training materials</li>
              <li>• Documentation</li>
            </ul>
          </div>
          
          <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
            <h4 className="text-purple-300 font-semibold mb-2">Demonstrate</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Commercial viability</li>
              <li>• Client satisfaction</li>
              <li>• Operational efficiency</li>
              <li>• Scalability readiness</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Learn</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Edge cases and exceptions</li>
              <li>• Performance bottlenecks</li>
              <li>• User feedback</li>
              <li>• Improvement opportunities</li>
            </ul>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Pilot Timeline</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Phase</th>
                <th className="pb-2">Duration</th>
                <th className="pb-2">Clients</th>
                <th className="pb-2">Focus</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 text-emerald-400">Week 1</td>
                <td>5 days</td>
                <td>Internal only</td>
                <td>Staff validation, process testing</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-blue-400">Week 2-3</td>
                <td>10 days</td>
                <td>5 pilot clients</td>
                <td>Initial client feedback, bug fixing</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-purple-400">Week 4-6</td>
                <td>15 days</td>
                <td>20 clients</td>
                <td>Scale testing, performance validation</td>
              </tr>
              <tr>
                <td className="py-2 text-amber-400">Week 7+</td>
                <td>Ongoing</td>
                <td>Full rollout</td>
                <td>General availability</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DocumentSection>

      {/* Readiness Criteria */}
      <DocumentSection title="Readiness Criteria" icon={CheckCircle} id="readiness-criteria">
        <p className="text-slate-300 mb-6">
          All readiness criteria must be met before each pilot phase can proceed.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Technical Readiness</h3>
        
        <Checklist 
          title="Technical Requirements"
          items={[
            'All core features implemented and tested',
            'Security hardening complete (httpOnly cookies, rate limiting)',
            'Performance testing completed and acceptable',
            'Backup and recovery procedures verified',
            'Monitoring and alerting configured',
            'WebSocket fallback (polling) operational',
            'Error handling and boundaries in place',
            'Audit logging complete'
          ]}
        />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Operational Readiness</h3>
        
        <Checklist 
          title="Operational Requirements"
          items={[
            'Support team trained and ready',
            'Escalation procedures documented',
            'SLA definitions finalized',
            'Contractor network coverage confirmed',
            'Client onboarding process validated',
            'Communication templates prepared',
            'Incident management process tested'
          ]}
        />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Documentation Readiness</h3>
        
        <Checklist 
          title="Documentation Requirements"
          items={[
            'User guides complete',
            'SOPs published and reviewed',
            'Governance documentation complete',
            'Training materials prepared',
            'FAQ and troubleshooting guides ready'
          ]}
        />

        <InfoBox title="Go/No-Go Decision">
          All checklist items must be completed before the pilot phase can proceed.
          The Operations Director makes the final go/no-go decision.
        </InfoBox>
      </DocumentSection>

      {/* Rollout Phases */}
      <DocumentSection title="Rollout Phases" icon={Rocket} id="rollout-phases">
        <p className="text-slate-300 mb-6">
          Each phase has specific entry criteria, activities, and exit criteria.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Phase 1: Internal Validation</h3>
        
        <SOPStep 
          number={1}
          title="Entry Criteria"
          description="Before starting internal validation."
          substeps={[
            'All technical readiness criteria met',
            'Test environment matches production',
            'Internal team accounts created',
            'Sample data loaded'
          ]}
        />

        <SOPStep 
          number={2}
          title="Activities"
          description="What happens during Phase 1."
          substeps={[
            'End-to-end process testing by staff',
            'Identify and fix blockers',
            'Refine workflows based on feedback',
            'Update documentation as needed'
          ]}
        />

        <SOPStep 
          number={3}
          title="Exit Criteria"
          description="Must be met to proceed to Phase 2."
          substeps={[
            'All critical bugs fixed',
            'Staff sign-off on readiness',
            'No blocking issues remaining',
            'Support process validated'
          ]}
        />

        <div className="my-6 border-t border-slate-700"></div>

        <h3 className="text-lg font-semibold text-white mb-4">Phase 2: Initial Clients (5 Clients)</h3>
        
        <SOPStep 
          number={1}
          title="Client Selection"
          description="Criteria for pilot client selection."
          substeps={[
            'Strong existing relationship',
            'Willing to provide feedback',
            'Moderate portfolio size (50-200 properties)',
            'Representative of target market'
          ]}
        />

        <SOPStep 
          number={2}
          title="Activities"
          description="What happens during Phase 2."
          substeps={[
            'Client onboarding with enhanced support',
            'Daily check-ins with clients',
            'Rapid bug fixing and iteration',
            'Collect structured feedback'
          ]}
        />

        <SOPStep 
          number={3}
          title="Exit Criteria"
          description="Must be met to proceed to Phase 3."
          substeps={[
            'All 5 clients live and operational',
            'Client satisfaction score >4/5',
            'SLA compliance >90%',
            'No critical issues open'
          ]}
        />

        <div className="my-6 border-t border-slate-700"></div>

        <h3 className="text-lg font-semibold text-white mb-4">Phase 3: Expanded Pilot (20 Clients)</h3>
        
        <SOPStep 
          number={1}
          title="Activities"
          description="What happens during Phase 3."
          substeps={[
            'Standard onboarding process',
            'Weekly review meetings',
            'Performance monitoring at scale',
            'Process optimization'
          ]}
        />

        <SOPStep 
          number={2}
          title="Exit Criteria"
          description="Must be met for general availability."
          substeps={[
            'All 20 clients operational',
            'Platform stable under load',
            'Support processes scaling',
            'Positive unit economics demonstrated'
          ]}
        />
      </DocumentSection>

      {/* Monitoring & KPIs */}
      <DocumentSection title="Monitoring & KPIs" icon={BarChart} id="monitoring-kpis">
        <p className="text-slate-300 mb-6">
          Key metrics tracked throughout the pilot to measure success.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Success Metrics</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Metric</th>
                <th className="pb-2">Target</th>
                <th className="pb-2">Minimum</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Login Success Rate</td>
                <td className="text-emerald-400">99.5%</td>
                <td className="text-amber-400">99%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">API Error Rate</td>
                <td className="text-emerald-400">&lt;0.5%</td>
                <td className="text-amber-400">&lt;1%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">SLA Compliance</td>
                <td className="text-emerald-400">&gt;95%</td>
                <td className="text-amber-400">&gt;90%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">First-Time Fix Rate</td>
                <td className="text-emerald-400">&gt;80%</td>
                <td className="text-amber-400">&gt;70%</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Client Satisfaction (NPS)</td>
                <td className="text-emerald-400">&gt;50</td>
                <td className="text-amber-400">&gt;30</td>
              </tr>
              <tr>
                <td className="py-2">Support Ticket Resolution</td>
                <td className="text-emerald-400">&lt;4 hours</td>
                <td className="text-amber-400">&lt;8 hours</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Monitoring Dashboard</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2">Technical Health</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• API response times (p50, p95, p99)</li>
              <li>• Error rates by endpoint</li>
              <li>• Database query performance</li>
              <li>• WebSocket connection status</li>
            </ul>
          </div>
          
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
            <h4 className="text-slate-300 font-semibold mb-2">Operational Health</h4>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>• Jobs created per day</li>
              <li>• SLA compliance rates</li>
              <li>• Contractor response times</li>
              <li>• Support ticket volume</li>
            </ul>
          </div>
        </div>

        <WarningBox title="Alert Thresholds">
          Automated alerts trigger when metrics fall below minimum thresholds.
          All alerts require acknowledgement and documented resolution.
        </WarningBox>
      </DocumentSection>

      {/* Escalation & Support */}
      <DocumentSection title="Escalation & Support" icon={Users} id="escalation-support">
        <p className="text-slate-300 mb-6">
          Enhanced support structure during the pilot period.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Pilot Support Structure</h3>
        <EscalationPath levels={['Helpdesk (First Line)', 'Pilot Lead', 'Operations Manager', 'Director']} />

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Support Response Times</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Priority</th>
                <th className="pb-2">Standard</th>
                <th className="pb-2">Pilot (Enhanced)</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 text-red-400">Critical</td>
                <td>1 hour</td>
                <td className="text-emerald-400">30 mins</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-amber-400">High</td>
                <td>4 hours</td>
                <td className="text-emerald-400">2 hours</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 text-blue-400">Medium</td>
                <td>8 hours</td>
                <td className="text-emerald-400">4 hours</td>
              </tr>
              <tr>
                <td className="py-2 text-slate-400">Low</td>
                <td>24 hours</td>
                <td className="text-emerald-400">8 hours</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Checklist 
          title="Pilot Support Checklist"
          items={[
            'Dedicated Slack/Teams channel for pilot clients',
            'Daily standup to review pilot status',
            'Weekly pilot review meeting scheduled',
            'Direct escalation path to engineering',
            'Feedback collection mechanism active',
            'Issue tracking with pilot tag'
          ]}
        />
      </DocumentSection>
    </GovernancePageLayout>
  );
}
